import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { recordPage, goBack } from '../../utils/trail';
import logo from '../../titan-logo.jpg';
import { setPreferredView } from '../../services/viewMode';
import ChangePinModal from '../../components/ChangePinModal';
import { ReportIssueModal } from '../../components/ReportIssue';
import { SaveRecipientsHost } from '../../components/ContactCheck';   // 'Add to the Customer List?' after an email (Oct 6 2026)
import MailboxPrompt from '../../components/MailboxPrompt';
import PreviewRoleContext from '../../context/PreviewRoleContext';
import { COS_USER_IDS } from '../../cosUsers';
import NotificationBell from '../../components/NotificationBell';
import { AskStrip } from '../../components/AskCards';
import EmbedContext from '../../context/EmbedContext';


const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
function apiFetch(path, opts = {}) {
  return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'x-worker-token': getToken(), 'Content-Type': 'application/json', ...(opts.headers || {}) } }).then(r => r.json());
}

function getSessionRole() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.role || 'admin'; } catch { return 'admin'; }
}
// The current signed-in user's id (staff logins store the user in te_worker).
function getSessionUserId() {
  try { return JSON.parse(localStorage.getItem('te_worker') || 'null')?.id || null; } catch { return null; }
}

// PM & Accounting have full office access. The two dashboards stay split so neither
// role sees a duplicate "Dashboard": PM keeps pm-dashboard, Accounting gets the admin one.
const NAV_ACCESS = {
  'directives':             ['admin', 'Executive'],
  'dashboard':              ['admin', 'Accounting'],
  'pm-dashboard':           ['ProjectManager'],
  'bids':                   ['admin', 'ProjectManager', 'OfficeStaff', 'Accounting'],
  'new-projects':           ['admin', 'OfficeStaff', 'ProjectManager', 'Accounting'],
  'schedule':               ['admin', 'OfficeStaff', 'ProjectManager', 'Accounting'],
  'workflows':              ['admin', 'ProjectManager', 'Accounting'],
  'shop':                   ['admin', 'ProjectManager', 'Accounting'],
  'administration':         ['admin', 'ProjectManager', 'Accounting'],
  'settings':               ['admin', 'ProjectManager', 'Accounting'],
  // Preview tools render the app AS another person, so they stay with the owner and
  // Executives. navRole maps Executive → 'admin', so 'admin' already covers an
  // Executive; the explicit entry is belt and braces if that mapping ever changes.
  'mobile-preview':         ['admin', 'Executive'],
  'desktop-preview':        ['admin', 'Executive'],
  'conversations':          ['admin', 'ProjectManager', 'Accounting'],
  'company-conversations':  ['admin', 'OfficeStaff', 'Accounting', 'ProjectManager'],
  'ask-ai':                 ['admin', 'OfficeStaff', 'Accounting', 'ProjectManager'],
  // Employee files are the Executive's alone (Ryan, Sep 2026) — the server refuses
  // edits from anyone else too; this just keeps the page out of other menus.
  'employees':              ['admin'],
  'purchase-orders':        ['admin', 'OfficeStaff'],
  'timesheets':             ['admin', 'Accounting'],
  'clients':                ['admin', 'OfficeStaff', 'ProjectManager', 'Accounting'],   // office = everything a PM has (Ryan, Sep 17 2026)
  'vendors':                ['admin'],
  // Same people who reach Shop — Safety was a tile in there until Sep 2026.
  'safety':                 ['admin', 'ProjectManager', 'Accounting'],
};

const ALL_NAV = [
  {
    id: 'directives',
    label: 'Executive',
    path: '/admin/directives',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M9 1.5l2.2 4.6 5 .7-3.6 3.5.9 5L9 12.9 4.5 15.3l.9-5L1.8 6.8l5-.7L9 1.5z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" fill="none"/>
      </svg>
    ),
  },
  {
    // Private line to the external OpenClaw Chief of Staff — only the two
    // allowlisted users see this (gated by user id in the NAV filter below).
    id: 'chief-of-staff',
    label: 'Chief of Staff',
    path: '/admin/cos',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M9 2.2c-1.1 0-2 .9-2 2 0 .7.36 1.32.9 1.68C6.7 6.3 6 7.3 6 8.5V10h6V8.5c0-1.2-.7-2.2-1.9-2.62.54-.36.9-.98.9-1.68 0-1.1-.9-2-2-2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" fill="none"/>
        <path d="M4.5 12h9l.8 3.2a.6.6 0 01-.58.8H4.28a.6.6 0 01-.58-.8L4.5 12z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" fill="none"/>
      </svg>
    ),
  },
  {
    id: 'pm-dashboard',
    label: 'Dashboard',
    path: '/admin/pm-dashboard',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="1" y="1" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <rect x="10" y="1" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <rect x="1" y="10" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <rect x="10" y="10" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
      </svg>
    ),
  },
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/admin',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="1" y="1" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <rect x="10" y="1" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <rect x="1" y="10" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <rect x="10" y="10" width="7" height="7" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
      </svg>
    ),
  },
  {
    id: 'bids',
    label: 'Bids',
    path: '/admin/bids',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M3 2h9l3 3v11a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
        <path d="M12 2v4h4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M5 9h8M5 12h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
        <path d="M5 6h4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    id: 'new-projects',
    label: 'Projects',
    path: '/admin/new-projects',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="1" y="3" width="16" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <path d="M5 3V1M13 3V1M1 7h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M9 10v4M7 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    id: 'schedule',
    // Full name is "Schedule and General Information" (the page title); the sidebar is
    // 220px and doesn't wrap, so the menu carries the short form.
    label: 'Schedule & General Info',
    path: '/admin/schedule',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="3" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <path d="M2 7h14M6 2v3M12 2v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M5 10h2M8 10h2M11 10h2M5 13h2M8 13h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    id: 'workflows',
    label: 'Workflows',
    path: '/admin/workflows',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="1.5" y="2.5" width="4" height="13" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <rect x="7" y="2.5" width="4" height="9" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <rect x="12.5" y="2.5" width="4" height="6" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
      </svg>
    ),
  },
  {
    id: 'shop',
    label: 'Shop',
    path: '/admin/shop',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M2 7l1.5-3.5A1 1 0 014.4 3h9.2a1 1 0 01.9.5L16 7" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
        <rect x="2" y="7" width="14" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <path d="M6 15v-4h6v4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    // Its own page since Sep 2026 — it was a tile inside Shop.
    id: 'safety',
    label: 'Safety',
    path: '/admin/safety',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M9 1.8l6 2.2v4.6c0 3.6-2.4 6.4-6 7.6-3.6-1.2-6-4-6-7.6V4l6-2.2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="none"/>
        <path d="M6.3 9.1l2 2 3.5-3.7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    id: 'settings',
    label: 'Settings',
    path: '/admin/settings',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <path d="M9 1.5v2M9 14.5v2M1.5 9h2M14.5 9h2M3.8 3.8l1.4 1.4M12.8 12.8l1.4 1.4M14.2 3.8l-1.4 1.4M5.2 12.8l-1.4 1.4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    id: 'administration',
    label: 'Administration',
    path: '/admin/administration',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="5" width="14" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <path d="M6 5V3.5A1.5 1.5 0 017.5 2h3A1.5 1.5 0 0112 3.5V5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <path d="M2 9h14" stroke="currentColor" strokeWidth="1.3"/>
      </svg>
    ),
  },
  {
    id: 'conversations',
    label: 'Conversations',
    path: '/admin/conversations',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M2 3a1 1 0 011-1h12a1 1 0 011 1v8a1 1 0 01-1 1H6l-4 3V3z" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
        <path d="M5 7h8M5 10h5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    id: 'company-conversations',
    label: 'Company Chat',
    path: '/admin/company-conversations',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M2 3a1 1 0 011-1h12a1 1 0 011 1v8a1 1 0 01-1 1H6l-4 3V3z" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
        <circle cx="6" cy="7" r="1" fill="currentColor"/><circle cx="9" cy="7" r="1" fill="currentColor"/><circle cx="12" cy="7" r="1" fill="currentColor"/>
      </svg>
    ),
  },
  {
    // Desktop Ask AI — renders inside this layout (sidebar stays) and calls the
    // SAME askAssistant → /ai/ask API the mobile /ask page uses. No AI/API changes.
    id: 'ask-ai',
    label: 'Ask AI',
    path: '/admin/ask',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M2 3a1 1 0 011-1h12a1 1 0 011 1v8a1 1 0 01-1 1H6l-4 3V3z" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinejoin="round"/>
        <path d="M9 5.1l.65 1.75L11.4 7.5l-1.75.65L9 9.9l-.65-1.75L6.6 7.5l1.75-.65L9 5.1z" stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" fill="none"/>
      </svg>
    ),
  },
  {
    id: 'mobile-preview',
    label: 'Mobile Preview',
    path: '/admin/mobile-preview',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="5" y="1.5" width="8" height="15" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <line x1="7.5" y1="14" x2="10.5" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    ),
  },
  {
    id: 'desktop-preview',
    label: 'Desktop Preview',
    path: '/admin/desktop-preview',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="1.5" y="2.5" width="15" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
        <line x1="6" y1="15.5" x2="12" y2="15.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="9" y1="12.5" x2="9" y2="15.5" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
  },
];

// The desktop pages a person can open, in sidebar order — the phone's "Desktop pages"
// tile lists them (Ryan, Oct 1 2026). Same access rule as the sidebar itself.
export function desktopPagesFor(role, userId) {
  const navRole = role === 'Executive' ? 'admin' : role;
  return ALL_NAV.filter(item => {
    if (item.id === 'chief-of-staff') return COS_USER_IDS.includes(userId);
    return (NAV_ACCESS[item.id] || ['admin']).includes(navRole);
  }).map(item => ({ id: item.id, label: item.label, path: item.path, icon: item.icon }));
}

// onBack: a page may say where its Back goes (the job page: the Projects list — Pat, Oct 6 2026);
// without one it is the browser's back, as always.
// Inside a dashboard window (EmbedContext) a page is drawn on its own, without the menu around it.
export default function AdminLayout(props) {
  const embedded = useContext(EmbedContext);
  return embedded ? <>{props.children}</> : <AdminLayoutFull {...props} />;
}
function AdminLayoutFull({ children, onBack = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Every desktop page leaves its footprint, so Back goes to the page you were on (Ryan, Nov 1 2026).
  useEffect(() => { recordPage(location); }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps
  // On a phone the open sidebar takes most of the screen, so it starts folded there
  // (the button still opens it). Desktop starts open as before.
  const [collapsed, setCollapsed] = useState(() => typeof window !== 'undefined' && window.innerWidth < 640);
  const [showChangePin, setShowChangePin] = useState(false);
  const [showReport, setShowReport] = useState(() => { try { return new URLSearchParams(window.location.search).get('report') === '1'; } catch { return false; } });   // ?report=1 opens it (a Company Answer's Take me there)   // 🐞 Report app issue or request, from any desktop page
  // Inside Desktop Preview, render as the previewed role instead of the real session.
  const previewRole = useContext(PreviewRoleContext);
  const inPreview = !!previewRole;
  const role = previewRole || getSessionRole();
  // Executive mirrors the Admin nav exactly (one Dashboard) — plus Directives.
  const navRole = role === 'Executive' ? 'admin' : role;
  const NAV = ALL_NAV.filter(item => {
    if (item.id === 'chief-of-staff') return COS_USER_IDS.includes(getSessionUserId());
    return (NAV_ACCESS[item.id] || ['admin']).includes(navRole);
  });

  function isActive(path) {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  }

  return (
    <div className="admin-shell">
      {/* Sidebar */}
      <aside className={`admin-sidebar ${collapsed ? 'collapsed' : ''}`}>
        <div className="sidebar-logo-wrap">
          <img src={logo} alt="Titan Electrical" className="sidebar-logo" />
          <button className="sidebar-collapse-btn" onClick={() => setCollapsed(c => !c)} aria-label="Toggle sidebar">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d={collapsed ? "M6 4l4 4-4 4" : "M10 4L6 8l4 4"} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="sidebar-section-label">{!collapsed && 'MAIN MENU'}</div>

        <nav className="sidebar-nav">
          {NAV.map(item => (
            <button
              key={item.id}
              className={`sidebar-nav-item ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => navigate(item.path, isActive(item.path) ? { state: { navReset: Date.now() } } : undefined)}
              title={collapsed ? item.label : ''}
            >
              <span className="sidebar-nav-icon">{item.icon}</span>
              {!collapsed && <span className="sidebar-nav-label">{item.label}</span>}
              {!collapsed && isActive(item.path) && <span className="sidebar-active-bar" />}
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          {!collapsed && (
            <div style={{ padding: '4px 12px 8px', display: 'flex', justifyContent: 'flex-end' }}>
              <NotificationBell />
            </div>
          )}
          {['ProjectManager', 'Accounting', 'OfficeStaff', 'FieldSupervisor', 'Executive'].includes(role) && (
            <button className="sidebar-nav-item" onClick={() => { if (inPreview) return; setPreferredView('mobile'); navigate('/dashboard'); }} title={collapsed ? 'Mobile view' : ''}>
              <span className="sidebar-nav-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <rect x="5" y="1.5" width="8" height="15" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <line x1="7.5" y1="14" x2="10.5" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </span>
              {!collapsed && <span className="sidebar-nav-label">Mobile view</span>}
            </button>
          )}
          {/* On every desktop view (Pat, Sep 28 2026), not only the job banner. */}
          <button className="sidebar-nav-item" onClick={() => setShowReport(true)} title={collapsed ? 'Report app issue or request' : 'Something wrong in the app, or something you want it to do? Tell the office.'}>
            <span className="sidebar-nav-icon">🐞</span>
            {!collapsed && <span className="sidebar-nav-label">Report app issue</span>}
          </button>
          {role !== 'admin' && (
            <button className="sidebar-nav-item" onClick={() => setShowChangePin(true)} title={collapsed ? 'Change PIN' : ''}>
              <span className="sidebar-nav-icon">🔑</span>
              {!collapsed && <span className="sidebar-nav-label">Change PIN</span>}
            </button>
          )}
          <button className="sidebar-nav-item" onClick={() => { if (inPreview) return; localStorage.clear(); navigate('/'); }} title={collapsed ? 'Sign out' : ''}>
            <span className="sidebar-nav-icon">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M7 3H3a1 1 0 00-1 1v10a1 1 0 001 1h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
                <path d="M12 12l3-3-3-3M6 9h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </span>
            {!collapsed && <span className="sidebar-nav-label">Sign out</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="admin-main">
        <div className="admin-content">
          <button onClick={() => (onBack ? onBack() : goBack(navigate))} title={onBack ? "Back" : "Back to the page you were on"}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#fff', border: '1px solid var(--border, #e5e7eb)', color: 'var(--gray-muted, #6b7280)', borderRadius: 8, padding: '6px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: 14 }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; e.currentTarget.style.color = 'var(--text, #111827)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = 'var(--gray-muted, #6b7280)'; }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M8.5 3L4.5 7l4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Back
          </button>
          {/* People land on different office pages, so "the office is waiting on your answer"
              rides along the top of all of them until it is answered. Not inside the previews. */}
          {!inPreview && <AskStrip onOpen={() => navigate('/my-asks')} />}
          {children}
        </div>
      </main>
      {showChangePin && <ChangePinModal onClose={() => setShowChangePin(false)} />}
      {showReport && <ReportIssueModal onClose={() => setShowReport(false)} />}
      <SaveRecipientsHost />
      {/* First desktop sign-in without a mailbox on file → ask once to connect it. */}
      <MailboxPrompt />
    </div>
  );
}
